<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Building2, Palette, Ghost } from 'lucide-vue-next'
import { useRoute } from 'vue-router'
import AppModal from '@/components/shared/AppModal.vue'
import OddsPreferences from './OddsPreferences.vue'
import BetPreferences from './BetPreferences.vue'
import DisplayPreferences from './DisplayPreferences.vue'
import HiddenTemplatesPreferences from './HiddenTemplatesPreferences.vue'

const props = defineProps<{
  visible: boolean
  initialTab?: 'preferences' | 'display'
  scrollTo?: string
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
}>()

const route = useRoute()
const preferencesRef = ref<any>(null)

const isOdds = computed(() => route.path.startsWith('/odds'))
const activeTab = ref<'preferences' | 'display'>('preferences')

// Handle save on close
watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible && props.initialTab) {
      activeTab.value = props.initialTab
      if (props.scrollTo) {
        setTimeout(() => {
          const el = document.getElementById(props.scrollTo!)
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }, 300) // Wait for tab transition
      }
    }
    if (!newVisible && preferencesRef.value?.save) {
      preferencesRef.value.save()
    }
  }
)

watch(
  () => props.initialTab,
  (newTab) => {
    if (newTab) {
      activeTab.value = newTab
    }
  }
)

function close() {
  emit('update:visible', false)
}
</script>

<template>
  <AppModal
    :visible="props.visible"
    @update:visible="emit('update:visible', $event)"
    @close="close"
    title="Preferences"
    description="Customise how you interact with the platform"
    has-sidebar
    :z-index="2000"
  >
    <template #sidebar>
      <nav class="grid grid-cols-2 md:flex md:flex-col gap-1 max-md:pb-2">
        <button 
          @click="activeTab = 'preferences'"
          :class="[
            'flex-1 flex items-center justify-center md:justify-start gap-3 px-4 py-2 md:py-3 rounded-xl transition-all font-medium text-sm whitespace-nowrap',
            activeTab === 'preferences' 
              ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20 font-bold' 
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'
          ]"
        >
          <Building2 class="w-4 h-4" />
          Bookmaker
        </button>
        
        <button 
          @click="activeTab = 'display'"
          :class="[
            'flex-1 flex items-center justify-center md:justify-start gap-3 px-4 py-2 md:py-3 rounded-xl transition-all font-medium text-sm whitespace-nowrap',
            activeTab === 'display' 
              ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20 font-bold' 
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'
          ]"
        >
          <Palette class="w-4 h-4" />
          Display
        </button>
      </nav>
    </template>

    <Transition name="fade-slide" mode="out-in">
      <div v-if="activeTab === 'preferences'" key="preferences" class="flex flex-col flex-1 min-h-0 overflow-x-hidden">
        <div class="flex flex-col flex-1 min-h-0 overflow-x-hidden">
          <OddsPreferences v-if="isOdds" ref="preferencesRef" />
          <BetPreferences v-else ref="preferencesRef" />
        </div>
      </div>
      <div v-else-if="activeTab === 'display'" key="display" class="flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <DisplayPreferences />
      </div>
    </Transition>
  </AppModal>
</template>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

</style>
